/* screens/kitchen/registries/RecipeRegistry.jsx — вкладки «ТТК» и «Полуфабрикаты».
   ЕДИНЫЙ компонент с prop kind ('dish'|'semiproduct') — фикс боли №5
   (нет TYPE_TO_TAB-костыля; kindBadge на карточке явно маркирует тип,
   подсветка активной вкладки — по ?tab=, см. KitchenShell).
   Фильтры: Статус + Аллерген. Сортировка: имя/себест./ккал.
   ХЕНДОФФ → frontend/pages/kitchen/RecipesTab.jsx (prop kind). */

import React from 'react';
import { useConfirm } from '../../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../../app/providers/ToastProvider.jsx';
import { RecipeCard, SkeletonGrid } from '../../../components/patterns/Cards.jsx';
import { EmptyState, QueryBoundary } from '../../../components/patterns/States.jsx';
import { KitchenFilterDrawer } from '../../../components/patterns/kitchen/KitchenFilterDrawer.jsx';
import { KitchenToolbar } from '../../../components/patterns/kitchen/KitchenToolbar.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { hooks } from '../../../hooks/index.jsx';
import { useRegistrySelection } from '../../../hooks/useRegistrySelection.jsx';
import { bulkDeleteTTK, downloadFile, listMenuBriefs, listTTKBriefs, ttkBulkExportUrl } from '../../../lib/api/index.js';
import { EU_ALLERGENS } from '../../../lib/adapters/ingredients.js';
import { RR } from '../../../lib/router.jsx';
import { enums as E } from '../../../lib/enums.js';

  const { useMemo, useState, useEffect } = React;
  const { useSearchParams } = RR;
  const { useFakeQuery, useDebouncedSearchParam, useDrawerStack } = hooks;

  const SORTS = [
    { value: 'name_asc', label: 'Название А–Я' },
    { value: 'name_desc', label: 'Название Я–А' },
    { value: 'cost_desc', label: 'Себест. ↓' },
    { value: 'cost_asc', label: 'Себест. ↑' },
    { value: 'kcal_desc', label: 'Ккал ↓' },
  ];
  // Подписи статусов — из ЕДИНОГО словаря E.STATUS_LABEL (P2-35): опция фильтра ↔
  // бейдж на карточке не должны расходиться. Перечислены только статусы рецептур.
  const STATUS_OPTS = ['ready', 'unbound', 'stub'].map((v) => ({ value: v, label: E.STATUS_LABEL[v] || v }));
  const readArr = (p, n) => { const v = p.get(n); return v ? v.split(',') : []; };
  const uuid = () => Math.random().toString(36).slice(2, 8);

  function RecipeRegistry({ kind = 'dish' }) {
    const [params, setParams] = useSearchParams();
    const [q, setQ] = useDebouncedSearchParam('q', 300);
    const { push } = useDrawerStack();
    const [filterOpen, setFilterOpen] = useState(false);
    const confirm = useConfirm();
    const toast = useToast();

    const isSemi = kind === 'semiproduct';
    const statuses = readArr(params, 'status');
    const allergens = readArr(params, 'allergen');
    // Фильтр по проекту меню — только для блюд (полуфабрикаты в меню не входят),
    // поэтому для semiproduct выбор всегда пуст (URL-параметр игнорируется).
    const menuSel = isSemi ? [] : readArr(params, 'menu');
    const sort = params.get('sort') || 'name_asc';
    const mockErr = params.get('mock_error'); const mock403 = params.get('mock_403');

    const setParam = (name, arr) => setParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (Array.isArray(arr) ? arr.length : arr) sp.set(name, Array.isArray(arr) ? arr.join(',') : arr);
      else sp.delete(name);
      return sp;
    });
    // сброс — status/allergen/menu (не трогает q)
    const resetFilters = () => setParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete('status'); sp.delete('allergen'); sp.delete('menu'); return sp;
    });

    const query = useFakeQuery('recipes-' + kind, () => listTTKBriefs({ kind }), [kind]);
    // Меню пространства — опции фильтра «Проект меню» (только для блюд).
    const menusQuery = useFakeQuery(
      'menus-filter', () => (isSemi ? Promise.resolve([]) : listMenuBriefs()), [isSemi]);
    const menuOpts = useMemo(
      () => (menusQuery.data || []).map((m) => ({ value: String(m.id), label: m.name || ('Меню #' + m.id) })),
      [menusQuery.data]);
    useEffect(() => {
      const h = () => query.reload();
      window.addEventListener('kitchen:ttks-changed', h);
      return () => window.removeEventListener('kitchen:ttks-changed', h);
    }, [query]);

    const list = useMemo(() => {
      const rows = (query.data || []).filter((r) => r.kind === kind);
      const needle = (q || '').trim().toLowerCase();
      let filtered = rows.filter((it) => {
        if (needle && !it.title.toLowerCase().includes(needle)) return false;
        if (statuses.length && !statuses.includes(it.status)) return false;
        if (allergens.length && !allergens.some((a) => (it.allergens || []).includes(a))) return false;
        if (menuSel.length) {
          const mids = it.menu_ids || [];
          const wantNone = menuSel.includes('__none__') && mids.length === 0;
          const wantAny = menuSel.some((m) => m !== '__none__' && mids.includes(Number(m)));
          if (!wantNone && !wantAny) return false;
        }
        return true;
      });
      const n = (v) => (v == null ? -1 : v);
      filtered = [...filtered].sort((a, b) => {
        switch (sort) {
          case 'name_desc': return b.title.localeCompare(a.title, 'ru');
          case 'cost_desc': return n(b.cost_per_portion_rub) - n(a.cost_per_portion_rub);
          case 'cost_asc': return n(a.cost_per_portion_rub) - n(b.cost_per_portion_rub);
          case 'kcal_desc': return n(b.kcal_per_portion) - n(a.kcal_per_portion);
          default: return a.title.localeCompare(b.title, 'ru');
        }
      });
      return filtered;
    }, [query.data, kind, q, statuses.join(','), allergens.join(','), menuSel.join(','), sort]);

    // F41: множественный выбор — общий хук (prune по списку + сброс при смене
    // вкладки kind, т.к. компонент не размонтируется между ТТК↔п/ф).
    const sel = useRegistrySelection(list, { resetKey: kind });

    const hasFilters = statuses.length > 0 || allergens.length > 0 || menuSel.length > 0 || !!(q && q.trim());
    const activeCount = statuses.length + allergens.length + menuSel.length;

    const createNew = () => {
      if (isSemi) { try { sessionStorage.setItem('kitchen:new-ttk', JSON.stringify({ kind: 'semiproduct' })); } catch (e) {} push('ttk', 'new-' + uuid()); }
      else push('ttk', 'new');
    };
    const toCard = (it) => ({
      kind: it.kind === 'semiproduct' ? 'semi' : 'dish',
      status: it.status, name: it.title,
      kcal: it.kcal_per_portion, yield_g: it.output_g, cost_rub: it.cost_per_portion_rub,
      allergens: it.allergens,
    });

    // ── F41: bulk-операции по выбранным (общий хук useRegistrySelection) ──
    const bulkDelete = async () => {
      const ids = [...sel.selected];
      if (!ids.length) return;
      const noun = isSemi ? 'полуфабрикат(ов)' : 'ТТК';
      const ok = await confirm({
        title: `Удалить выбранные (${ids.length})?`,
        body: `Будет удалено ${ids.length} ${noun} вместе с историей версий. Занятые как п/ф в других ТТК будут пропущены. Действие необратимо.`,
        confirmLabel: 'Удалить', tone: 'danger',
      });
      if (!ok) return;
      sel.setBusy(true);
      try {
        // Бэк лимитит 200 id за запрос — чанкуем, чтобы «Выбрать все» на
        // большом реестре не падал общим тостом.
        let deletedN = 0; let skippedN = 0;
        for (let i = 0; i < ids.length; i += 200) {
          const r = await bulkDeleteTTK(ids.slice(i, i + 200));
          if (!r.ok) throw new Error((r.data && r.data.detail) || 'bulk failed');
          deletedN += ((r.data && r.data.deleted) || []).length;
          skippedN += ((r.data && r.data.skipped) || []).length;
        }
        let msg = `Удалено: ${deletedN}`;
        if (skippedN) msg += `; пропущено: ${skippedN} (используются как п/ф)`;
        toast.show(msg, skippedN ? 'accent' : 'success');
        sel.exit();
        query.reload();
        window.dispatchEvent(new CustomEvent('kitchen:ttks-changed'));
      } catch (e) {
        toast.show((e && e.message) || 'Не удалось удалить выбранные', 'danger');
      } finally { sel.setBusy(false); }
    };

    // Скачивание blob'ом с X-Workspace-Id: window.open не шлёт заголовок,
    // и ТТК business-пространства ловили 404 «битым» файлом.
    const bulkExport = async () => {
      const ids = [...sel.selected];
      if (!ids.length) return;
      if (ids.length > 50) toast.show('Экспортируются первые 50 карт', 'accent');
      const r = await downloadFile(ttkBulkExportUrl(ids.slice(0, 50)));
      if (!r.ok) toast.show((r.data && r.data.detail) || 'Не удалось скачать архив', 'danger');
    };

    return (
      <div className="registry">
        {sel.selectMode ? (
          <div className="registry__bulkbar">
            <span className="t-body">Выбрано: {sel.count}</span>
            <Button size="sm" variant="secondary" onClick={sel.selectAll}>
              {sel.allSelected ? 'Снять всё' : 'Выбрать все'}
            </Button>
            <div className="grow" />
            <Button size="sm" variant="secondary" iconLeft="download" disabled={!sel.count || sel.busy} onClick={bulkExport}>Скачать DOCX</Button>
            <Button size="sm" variant="danger" iconLeft="trash" disabled={!sel.count || sel.busy} loading={sel.busy} onClick={bulkDelete}>Удалить</Button>
            <Button size="sm" variant="ghost" onClick={sel.exit} disabled={sel.busy}>Готово</Button>
          </div>
        ) : (
          <KitchenToolbar
            search={q} onSearch={setQ} searchPlaceholder={isSemi ? 'Поиск по полуфабрикатам…' : 'Поиск по ТТК…'}
            onFilter={() => setFilterOpen(true)} activeFilterCount={activeCount}
            sortOptions={SORTS} sortValue={sort} onSort={(v) => setParam('sort', v)}
            actionLabel={isSemi ? 'Создать полуфабрикат' : 'Создать ТТК'} onAction={createNew} />
        )}
        {!sel.selectMode && list.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="sm" variant="ghost" onClick={() => sel.setSelectMode(true)}>Выбрать несколько</Button>
          </div>
        )}

        <QueryBoundary
          loading={query.loading} error={query.error} onRetry={query.reload}
          empty={!query.loading && !query.error && list.length === 0}
          skeleton={<SkeletonGrid count={6} />}
          emptyEl={hasFilters
            ? <EmptyState serif={false} icon="search" title="Ничего не найдено"
                body="Под текущие фильтры рецептур нет."
                action={<Button variant="secondary" onClick={resetFilters}>Сбросить фильтры</Button>} />
            : (isSemi
              ? <EmptyState icon="box" title="Соберите первый полуфабрикат"
                  body="Полуфабрикаты (соусы, бульоны, заготовки) входят строкой в состав блюд. Правило: п/ф не содержит п/ф."
                  action={<Button variant="primary" iconLeft="plus" onClick={createNew}>Создать полуфабрикат</Button>} />
              : <EmptyState icon="utensils" title="Создайте первую ТТК"
                  body="Технико-технологическая карта по ГОСТ: состав, технология, КБЖУ и себестоимость считаются автоматически."
                  action={<Button variant="primary" iconLeft="plus" onClick={createNew}>Создать ТТК</Button>} />)}>
          <div className="tile-grid">
            {list.map((it) => (
              <RecipeCard key={it.id} item={toCard(it)} onClick={() => push('ttk', it.id)}
                selectable={sel.selectMode} selected={sel.selected.has(it.id)}
                onToggleSelect={() => sel.toggle(it.id)} />
            ))}
          </div>
        </QueryBoundary>

        <KitchenFilterDrawer
          open={filterOpen} onClose={() => setFilterOpen(false)}
          value={{ status: statuses, allergen: allergens, menu: menuSel }}
          onChange={(d) => setParams((prev) => {
            const sp = new URLSearchParams(prev);
            if (d.status && d.status.length) sp.set('status', d.status.join(',')); else sp.delete('status');
            if (d.allergen && d.allergen.length) sp.set('allergen', d.allergen.join(',')); else sp.delete('allergen');
            if (d.menu && d.menu.length) sp.set('menu', d.menu.join(',')); else sp.delete('menu');
            return sp;
          })}
          sections={[
            { key: 'status', title: 'Статус', options: STATUS_OPTS },
            ...(menuOpts.length ? [{
              key: 'menu', title: 'Проект меню',
              options: [...menuOpts, { value: '__none__', label: 'Без меню' }],
            }] : []),
            { key: 'allergen', title: 'Аллерген', options: EU_ALLERGENS.map((a) => ({ value: a, label: a })) },
          ]} />
      </div>
    );
  }

export { RecipeRegistry };
